import React from 'react';
import { CheckCircle, XCircle, Info, Shield } from 'lucide-react';
const SEVERITY_STYLES = {
  high: 'border-l-danger-red',
  medium: 'border-l-shinobi-orange',
  low: 'border-l-yellow-500',
  info: 'border-l-gray-600',
};

const STATUS_CONFIG = {
  pass: { icon: CheckCircle, color: 'text-neon-green', label: 'Present', bg: 'bg-neon-green/10' },
  fail: { icon: XCircle, color: 'text-danger-red', label: 'Missing', bg: 'bg-danger-red/10' },
  info: { icon: Info, color: 'text-slate-500', label: 'Info', bg: 'bg-gray-700/30' },
};

const SecurityHeaderResults = ({ results }) => {
  const isTrial = false;

  const visibleResults = isTrial && results.length > 1 ? results.slice(0, 1) : results;
  const hiddenResults = isTrial && results.length > 1 ? results.slice(1) : [];

  const passed = visibleResults.filter(r => r.data?.status === 'pass');
  const failed = visibleResults.filter(r => r.data?.status === 'fail');
  const info = visibleResults.filter(r => r.data?.status === 'info');

  const score = results.length > 0
    ? Math.round((passed.length / (passed.length + failed.length)) * 100) || 0
    : 0;
  const scoreColor = score >= 70 ? 'text-neon-green' : score >= 40 ? 'text-shinobi-orange' : 'text-danger-red';

  const renderHeader = (result) => {
    const data = result.data || {};
    const cfg = STATUS_CONFIG[data.status] || STATUS_CONFIG.info;
    const StatusIcon = cfg.icon;
    const severityBorder = SEVERITY_STYLES[data.severity] || SEVERITY_STYLES.info;

    return (
      <div
        key={result.id}
        className={`bg-white border border-slate-200 border-l-4 ${severityBorder} rounded-lg p-4 hover:border-slate-200 transition-colors`}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-3">
            <StatusIcon className={`w-5 h-5 flex-shrink-0 ${cfg.color}`} />
            <div>
              <h4 className="text-sm font-bold text-slate-800 font-mono">{data.header}</h4>
              {data.present && data.value && (
                <p className="text-xs text-gray-500 font-mono mt-1 break-all">{data.value}</p>
              )}
            </div>
          </div>
          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${cfg.bg} ${cfg.color}`}>
            {cfg.label}
          </span>
        </div>
        {data.recommendation && (
          <p className="text-xs text-gray-500 mt-2 ml-8">{data.recommendation}</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Score summary */}
      <div className="flex items-center space-x-6 px-1">
        <div className="flex items-center space-x-2">
          <Shield className={`w-5 h-5 ${scoreColor}`} />
          <span className={`text-2xl font-bold font-mono ${scoreColor}`}>{score}%</span>
          <span className="text-sm text-gray-500">header coverage</span>
        </div>
        <div className="flex items-center space-x-4 text-xs text-gray-500">
          <span className="flex items-center space-x-1">
            <CheckCircle className="w-3.5 h-3.5 text-neon-green" />
            <span>{passed.length} passed</span>
          </span>
          <span className="flex items-center space-x-1">
            <XCircle className="w-3.5 h-3.5 text-danger-red" />
            <span>{failed.length} missing</span>
          </span>
          {info.length > 0 && (
            <span className="flex items-center space-x-1">
              <Info className="w-3.5 h-3.5 text-slate-500" />
              <span>{info.length} info</span>
            </span>
          )}
        </div>
      </div>

      {/* Missing headers (failures first) */}
      {failed.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-danger-red uppercase tracking-wide mb-3">Missing Headers</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {failed.map(renderHeader)}
          </div>
        </div>
      )}

      {/* Present headers */}
      {passed.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-neon-green uppercase tracking-wide mb-3">Present Headers</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {passed.map(renderHeader)}
          </div>
        </div>
      )}

      {/* Info headers */}
      {info.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Informational</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {info.map(renderHeader)}
          </div>
        </div>
      )}

      {/* Trial blur for hidden headers */}
      {hiddenResults.length > 0 && (
        
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {hiddenResults.map(renderHeader)}
          </div>
        
      )}
    </div>
  );
};

export default SecurityHeaderResults;
